import { Flow, Text } from 'bold-ui'
import { SelectDownshiftMenuProps } from 'bold-ui/lib/components/Select/SelectSingle/SelectDownshiftMenu'
import { CustomSelectFieldProps, SelectField, SelectItem } from 'components/form/final-form'
import { HLabel } from 'components/HLabel'
import { CidSelectFieldDocument } from 'graphql/hooks.generated'
import { Cid10QueryInput, CidSelectFieldQuery, CidSelectFieldQueryVariables } from 'graphql/types.generated'
import { useMemo } from 'react'
import { CypressProps } from 'types/cypress'
import { emptyArrayOf } from 'util/array'

import { RenderGrupoSelectHeader } from '../RenderGrupoSelectHeader'
import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'

export type CidSelectFieldModel = CidSelectFieldQuery['cids']['content'][0]

export type CidSelectFieldProps = CustomSelectFieldProps<CidSelectFieldModel> &
  Pick<Cid10QueryInput, 'sexo' | 'parteBucal' | 'idadeCidadaoEmAnos' | 'somenteRelacionados'> &
  CypressProps & {
    ciapRelacionada?: ID
    renderGrupoCiapRelacionada?: boolean
  }

const ID_GRUPO_CIDS_RELACIONADAS = 'grupoCidsRelacionadas'
const ID_GRUPO_OUTRAS_CIDS = 'grupoOutrasCids'

const GRUPO_CIDS_RELACIONADAS_HEADER_ITEM: CidSelectFieldModel = {
  id: ID_GRUPO_CIDS_RELACIONADAS,
  ciapsRelacionadas: [],
  codigo: '',
  nome: '',
}

const GRUPO_OUTRAS_CIDS_HEADER_ITEM: CidSelectFieldModel = {
  id: ID_GRUPO_OUTRAS_CIDS,
  ciapsRelacionadas: [],
  codigo: '',
  nome: '',
}

export const cidItemToString = (item: CidSelectFieldModel) => item && `${item.nome} - ${item.codigo}`

export const cidRenderItem = (item: CidSelectFieldModel) =>
  item && (
    <Flow direction='vertical' gap={0} data-testid='CidSelectField.item'>
      <Text fontWeight='bold'>{item.nome}</Text>
      <HLabel title='Código '>{item.codigo}</HLabel>
    </Flow>
  )

export function CidSelectField(props: CidSelectFieldProps) {
  const {
    sexo,
    ciapRelacionada,
    renderGrupoCiapRelacionada = true,
    somenteRelacionados = false,
    parteBucal,
    idadeCidadaoEmAnos,
    dataTestId,
    itemIsDisabled,
    ...rest
  } = props

  const {
    selectProps: { items, ...asyncPropsRest },
  } = useAsyncQuerySelect<CidSelectFieldModel, CidSelectFieldQuery, CidSelectFieldQueryVariables>({
    query: CidSelectFieldDocument,
    extractItems: (data) => data?.cids?.content,
    variables: (inputQuery: string): CidSelectFieldQueryVariables => ({
      input: {
        query: inputQuery,
        sexo,
        ciapRelacionada,
        parteBucal,
        idadeCidadaoEmAnos,
        somenteRelacionados,
        pageParams: DEFAULT_SELECT_PAGE_PARAM,
      },
      fetchCiapsRelacionadas: !!ciapRelacionada,
    }),
  })

  const selectItems = useMemo<{
    hasCidsRelacionadas: boolean
    hasOutrasCids: boolean
    items: CidSelectFieldModel[]
  }>(() => {
    const sortedItems = (items ?? emptyArrayOf<CidSelectFieldModel>()).toSorted((a, b) => {
      return a.nome.localeCompare(b.nome)
    })

    const cidsRelacionadasComCiap: CidSelectFieldModel[] = renderGrupoCiapRelacionada
      ? [GRUPO_CIDS_RELACIONADAS_HEADER_ITEM]
      : []

    const outrasCid: CidSelectFieldModel[] = renderGrupoCiapRelacionada ? [GRUPO_OUTRAS_CIDS_HEADER_ITEM] : []

    sortedItems?.forEach((item) => {
      item.ciapsRelacionadas?.map((value) => value.id).includes(ciapRelacionada)
        ? cidsRelacionadasComCiap.push(item)
        : outrasCid.push(item)
    })

    return {
      hasCidsRelacionadas: cidsRelacionadasComCiap.length > 1,
      hasOutrasCids: outrasCid.length > 1,
      items: cidsRelacionadasComCiap.concat(outrasCid),
    }
  }, [items, renderGrupoCiapRelacionada, ciapRelacionada])

  return (
    <SelectField<CidSelectFieldModel>
      itemToString={cidItemToString}
      items={selectItems.items}
      renderItem={cidRenderItem}
      dataTestId={dataTestId}
      itemIsDisabled={itemIsDisabled}
      components={{
        Item: (
          itemProps: SelectDownshiftMenuProps<CidSelectFieldModel> & { item: CidSelectFieldModel; index: number }
        ) => {
          const renderGrupoCidsRelacionadas = itemProps.item?.id === ID_GRUPO_CIDS_RELACIONADAS
          const renderGrupoOutrasCid = itemProps.item?.id === ID_GRUPO_OUTRAS_CIDS

          if (renderGrupoOutrasCid) {
            return (
              <RenderGrupoSelectHeader
                titulo='Outras CID10'
                emptyMessage={!selectItems.hasOutrasCids && 'Nenhuma outra CID10.'}
                itemProps={itemProps.downshift.getItemProps({ item: itemProps.item, disabled: true })}
              />
            )
          }

          if (renderGrupoCidsRelacionadas) {
            return (
              <RenderGrupoSelectHeader
                titulo='CID10 relacionadas com a CIAP 2 selecionada'
                emptyMessage={
                  !ciapRelacionada
                    ? 'Nenhuma CIAP 2 selecionada.'
                    : !selectItems.hasCidsRelacionadas && 'Nenhuma CID10 relacionada.'
                }
                itemProps={itemProps.downshift.getItemProps({ item: itemProps.item, disabled: true })}
              />
            )
          }

          return (
            <SelectItem
              isAlreadySelected={itemIsDisabled ?? (() => false)}
              customText={undefined}
              selectedItemStyle={undefined}
              selectedItemTooltip={undefined}
              {...itemProps}
            />
          )
        },
      }}
      {...asyncPropsRest}
      {...rest}
    />
  )
}
